import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { registerWebMcp } from "@/lib/webmcp";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import Index from "./pages/Index";
import { ExtensionBanner } from "@/components/layout/ExtensionBanner";

const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Generate = lazy(() => import("./pages/Generate"));
const History = lazy(() => import("./pages/History"));
const HistoryDetail = lazy(() => import("./pages/HistoryDetail"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Settings = lazy(() => import("./pages/Settings"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const PromptLibrary = lazy(() => import("./pages/PromptLibrary"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Retry = lazy(() => import("./pages/Retry"));
const Billing = lazy(() => import("./pages/Billing"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminCredits = lazy(() => import("./pages/admin/AdminCredits"));
const AdminModels = lazy(() => import("./pages/admin/AdminModels"));
const AdminTemplates = lazy(() => import("./pages/admin/AdminTemplates"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminAIConfig = lazy(() => import("./pages/admin/AdminAIConfig"));
const AdminPromptLibrary = lazy(() => import("./pages/admin/AdminPromptLibrary"));
const AdminShowcases = lazy(() => import("./pages/admin/AdminShowcases"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const PoliticaCookies = lazy(() => import("./pages/PoliticaCookies"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Suporte = lazy(() => import("./pages/Suporte"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  useEffect(() => {
    registerWebMcp();
  }, []);

  return (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ExtensionBanner />
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/onboarding" element={
                <ProtectedRoute requireOnboarding={false}>
                  <Onboarding />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/generate" element={
                <ProtectedRoute>
                  <Generate />
                </ProtectedRoute>
              } />
              <Route path="/history" element={
                <ProtectedRoute>
                  <History />
                </ProtectedRoute>
              } />
              <Route path="/history/:id" element={
                <ProtectedRoute>
                  <HistoryDetail />
                </ProtectedRoute>
              } />
              <Route path="/favorites" element={
                <ProtectedRoute>
                  <Favorites />
                </ProtectedRoute>
              } />
              <Route path="/library" element={
                <ProtectedRoute>
                  <PromptLibrary />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/success/:planSlug" element={<CheckoutSuccess />} />
              <Route path="/checkout/:planSlug" element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              } />
              <Route path="/retry/:orderId" element={
                <ProtectedRoute>
                  <Retry />
                </ProtectedRoute>
              } />
              <Route path="/billing" element={
                <ProtectedRoute>
                  <Billing />
                </ProtectedRoute>
              } />
              {/* Admin routes - wrapped in AdminRoute for defense-in-depth */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />
              <Route path="/admin/users" element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              } />
              <Route path="/admin/credits" element={
                <AdminRoute>
                  <AdminCredits />
                </AdminRoute>
              } />
              <Route path="/admin/models" element={
                <AdminRoute>
                  <AdminModels />
                </AdminRoute>
              } />
              <Route path="/admin/templates" element={
                <AdminRoute>
                  <AdminTemplates />
                </AdminRoute>
              } />
              <Route path="/admin/subscriptions" element={
                <AdminRoute>
                  <AdminSubscriptions />
                </AdminRoute>
              } />
              <Route path="/admin/ai-config" element={
                <AdminRoute>
                  <AdminAIConfig />
                </AdminRoute>
              } />
              <Route path="/admin/prompt-library" element={
                <AdminRoute>
                  <AdminPromptLibrary />
                </AdminRoute>
              } />
              <Route path="/admin/showcases" element={
                <AdminRoute>
                  <AdminShowcases />
                </AdminRoute>
              } />
              <Route path="/termos" element={<TermosDeUso />} />
              <Route path="/privacidade" element={<PoliticaPrivacidade />} />
              <Route path="/cookies" element={<PoliticaCookies />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/suporte" element={<Suporte />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
